// client/src/components/wholesaler/UsageChart.js
// Visual chart component for usage trends (orders and fees over time)

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const UsageChart = ({ data }) => {
  const { t } = useTranslation(['wholesaler']);
  const [chartType, setChartType] = useState('orders'); // orders or fees

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t('subscription.dashboard.noDataAvailable', 'No data available')}
      </div>
    );
  }

  const values = data.map(d => chartType === 'orders' ? d.orders : d.fees);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const range = maxValue - minValue || 1;

  const getBarHeight = (value) => {
    if (maxValue === 0) return 0;
    return ((value / maxValue) * 100).toFixed(2);
  };

  const formatValue = (value) => {
    return chartType === 'orders' 
      ? value.toString()
      : `€${value.toFixed(2)}`;
  };

  const totalOrders = data.reduce((sum, d) => sum + d.orders, 0);
  const totalFees = data.reduce((sum, d) => sum + d.fees, 0);
  const avgOrders = (totalOrders / data.length).toFixed(1);
  const avgFees = (totalFees / data.length).toFixed(2);

  return (
    <div>
      {/* Chart Type Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setChartType('orders')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            chartType === 'orders'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t('subscription.dashboard.chart.orders', 'Orders')}
        </button>
        <button
          onClick={() => setChartType('fees')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            chartType === 'fees'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t('subscription.dashboard.chart.fees', 'Fees')}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 mb-1">
            {t('subscription.dashboard.chart.totalOrders', 'Total Orders')}
          </div>
          <div className="text-xl font-bold text-blue-600">{totalOrders}</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 mb-1">
            {t('subscription.dashboard.chart.totalFees', 'Total Fees')}
          </div>
          <div className="text-xl font-bold text-green-600">€{totalFees.toFixed(2)}</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 mb-1">
            {t('subscription.dashboard.chart.avgOrders', 'Avg Orders/Month')}
          </div>
          <div className="text-xl font-bold text-purple-600">{avgOrders}</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 mb-1">
            {t('subscription.dashboard.chart.avgFees', 'Avg Fees/Month')}
          </div>
          <div className="text-xl font-bold text-amber-600">€{avgFees}</div>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-end justify-around gap-2" style={{ height: '300px' }}>
          {data.map((monthData, index) => {
            const value = chartType === 'orders' ? monthData.orders : monthData.fees;
            const barHeight = getBarHeight(value);
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center justify-end" style={{ height: '100%' }}>
                {/* Value Label on Hover */}
                <div className="mb-2 opacity-0 hover:opacity-100 transition-opacity text-xs font-medium text-gray-700 text-center">
                  {formatValue(value)}
                </div>
                
                {/* Bar */}
                <div className="w-full flex flex-col justify-end items-center" style={{ height: '85%' }}>
                  <div
                    className={`w-full rounded-t-lg transition-all duration-300 hover:opacity-80 cursor-pointer ${
                      chartType === 'orders' ? 'bg-blue-500' : 'bg-green-500'
                    }`}
                    style={{ height: `${barHeight}%` }}
                    title={`${monthData.month}: ${formatValue(value)}`}
                  >
                    {/* Show value inside bar if tall enough */}
                    {barHeight > 20 && (
                      <div className="text-white text-xs font-medium text-center pt-2">
                        {formatValue(value)}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Month Label */}
                <div className="mt-2 text-xs text-gray-600 text-center w-full overflow-hidden">
                  <div className="truncate">{monthData.month}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Y-Axis Labels */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between text-xs text-gray-500">
          <span>Min: {formatValue(minValue)}</span>
          <span>Max: {formatValue(maxValue)}</span>
        </div>
      </div>

      {/* Insights */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">📊</div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">
                {t('subscription.dashboard.insights.trend', 'Trend Analysis')}
              </h4>
              <p className="text-sm text-gray-600">
                {data[data.length - 1][chartType === 'orders' ? 'orders' : 'fees'] > data[0][chartType === 'orders' ? 'orders' : 'fees']
                  ? t('subscription.dashboard.insights.increasing', 'Your usage is trending upward over the past 6 months')
                  : t('subscription.dashboard.insights.decreasing', 'Your usage is trending downward over the past 6 months')
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">
                {t('subscription.dashboard.insights.recommendation', 'Recommendation')}
              </h4>
              <p className="text-sm text-gray-600">
                {avgOrders > 150
                  ? t('subscription.dashboard.insights.considerUpgrade', 'Consider upgrading to save on per-order fees with high volume')
                  : t('subscription.dashboard.insights.currentTierGood', 'Your current tier is well-suited for your usage')
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageChart;